import { v4 as uuidv4 } from 'uuid';
import getApiConfig from '../config/apiConfig';
import { DEAL_STAGES } from '../constants/dealStages';
import { logAPIError } from '../utils/apiErrorLogger';

// Opportunity JSON Schema
const createEmptyOpportunity = (userEmail = '') => {
  return {
    opportunityId: uuidv4(),
    opportunityName: '',
    companyId: null,
    companyName: '',
    dealStage: DEAL_STAGES.LEAD, // Changed from 'Prospecting' to 'Lead'
    estimatedValue: 0,
    estimatedCloseDate: new Date().toISOString().split('T')[0],
    probability: 0,
    nextStep: '',
    competitorAnalysis: '',
    closedValue: null,
    closedDate: null,
    cancelledReason: '',
    description: '',
    contacts: [],
    contactIds: [], // Add this line to initialize contactIds array
    salesPersonEmails: [userEmail], // Initialize with the current user
    moments: [],
    comments: [],
    createdByEmail: '', // Don't set this initially for new opportunities
    createdDate: new Date().toISOString(),
    modifiedByEmail: '', // Don't set this initially for new opportunities
    modifiedDate: new Date().toISOString()
  };
};

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    let errorMessage;
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorData.message || 'An error occurred';
    } catch (parseError) {
      // If JSON parsing fails, fall back to text
      try {
        errorMessage = await response.text();
      } catch (textError) {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }
    }
    throw new Error(errorMessage);
  }
  
  try {
    return await response.json();
  } catch (parseError) {
    throw new Error('Failed to parse response as JSON');
  }
};

// Helper function to add moment to opportunity object
const addMoment = (opportunity, message, createdByEmail) => {
  const moment = {
    id: uuidv4(),
    message,
    createdByEmail,
    createdDate: new Date().toISOString()
  };
  
  if (!opportunity.moments) {
    opportunity.moments = [];
  }
  opportunity.moments.push(moment);
  
  return moment;
};

// Helper function to transform opportunity data
const transformOpportunity = (opportunity) => {
  const transformed = {
    opportunityId: opportunity.OpportunityId || opportunity.opportunityId || opportunity.Id || opportunity.id || '',
    opportunityName: opportunity.OpportunityName || opportunity.opportunityName || '',
    companyId: opportunity.CompanyId || opportunity.companyId || '',
    companyName: opportunity.CompanyName || opportunity.companyName || '',
    description: opportunity.Description || opportunity.description || '',
    dealStage: opportunity.DealStage || opportunity.dealStage || '',
    estimatedValue: opportunity.EstimatedValue || opportunity.estimatedValue,
    estimatedCloseDate: opportunity.EstimatedCloseDate || opportunity.estimatedCloseDate,
    probability: opportunity.Probability || opportunity.probability,
    nextStep: opportunity.NextStep || opportunity.nextStep || '',
    competitorAnalysis: opportunity.CompetitorAnalysis || opportunity.competitorAnalysis || '',
    closedValue: opportunity.ClosedValue || opportunity.closedValue,
    closedDate: opportunity.ClosedDate || opportunity.closedDate,
    cancelledReason: opportunity.CancelledReason || opportunity.cancelledReason || '',
    createdByEmail: opportunity.CreatedByEmail || opportunity.createdByEmail || '',
    createdDate: opportunity.CreatedDate || opportunity.createdDate || '',
    modifiedByEmail: opportunity.ModifiedByEmail || opportunity.modifiedByEmail || '',
    modifiedDate: opportunity.ModifiedDate || opportunity.modifiedDate || '',
    lineOfBusinessId: opportunity.LineOfBusinessId || opportunity.lineOfBusinessId || '',
    lineOfBusinessName: opportunity.LineOfBusinessName || opportunity.lineOfBusinessName || '',
    salesPersonEmails: opportunity.salesPersonEmails || [],
    contactIds: opportunity.contactIds || [],
    contacts: opportunity.contacts || [], // Add this line to preserve contacts
    comments: opportunity.comments || [],
    moments: opportunity.moments || [],
    // Add FirstSalesperson fields
    FirstSalespersonEmail: opportunity.FirstSalespersonEmail,
    FirstSalespersonName: opportunity.FirstSalespersonName,
    FirstSalespersonInitials: opportunity.FirstSalespersonInitials
  };

  // If we have recordsets from SQL Server, process them
  if (opportunity.recordsets) {
    // Contacts are in recordset[1]
    if (opportunity.recordsets[1]) {
      transformed.contacts = opportunity.recordsets[1];
      transformed.contactIds = opportunity.recordsets[1].map(c => c.ContactId || c.contactId);
    }

    // Salespersons are in recordset[2]
    if (opportunity.recordsets[2]) {
      transformed.salesPersonEmails = opportunity.recordsets[2].map(s => s.SalespersonEmail || s.salespersonEmail);
    }

    // Comments are in recordset[3]
    if (opportunity.recordsets[3]) {
      transformed.comments = opportunity.recordsets[3];
    }

    // Moments are in recordset[4]
    if (opportunity.recordsets[4]) {
      transformed.moments = opportunity.recordsets[4];
    }
  }

  // Ensure IDs are lowercase for consistency
  if (transformed.opportunityId) {
    transformed.opportunityId = transformed.opportunityId.toLowerCase();
  }
  if (transformed.companyId) {
    transformed.companyId = transformed.companyId.toLowerCase();
  }
  if (transformed.contactIds) {
    transformed.contactIds = transformed.contactIds.map(id => id && id.toLowerCase());
  }

  return transformed;
};

export const opportunityService = {
  // Create empty opportunity
  createEmptyOpportunity,

  // Add moment helper
  addMoment,

  // Get all opportunities
  async getOpportunities(pageSize = 10000) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/opportunities?pageSize=${pageSize}&pageNumber=1`);
      const data = await handleResponse(response);
      
      // The stored procedure returns two recordsets:
      // 1. The opportunities data
      // 2. The total count
      // If we get a recordsets array, use the first recordset
      let opportunities;
      if (data.recordsets && Array.isArray(data.recordsets)) {
        opportunities = data.recordsets[0];
      } else if (Array.isArray(data)) {
        opportunities = data;
      } else if (data.data && Array.isArray(data.data)) {
        opportunities = data.data;
      } else {
        console.error('Unexpected response format from opportunities API:', data);
        return [];
      }
      
      // Transform all opportunities to consistent format
      return opportunities.map(opportunity => transformOpportunity(opportunity));
    } catch (error) {
      logAPIError('opportunityService', 'getOpportunities', 'GET', error);
      throw error;
    }
  },

  // Get opportunity by ID
  async getOpportunity(opportunityId) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/opportunities/${opportunityId}`);
      const data = await handleResponse(response);
      
      // Transform the opportunity to consistent format
      const transformedOpportunity = transformOpportunity(data);
      
      // Ensure consistent casing for IDs
      if (transformedOpportunity.contactIds) {
        transformedOpportunity.contactIds = transformedOpportunity.contactIds.map(id => id.toLowerCase());
      }
      if (transformedOpportunity.contacts) {
        transformedOpportunity.contacts = transformedOpportunity.contacts.map(contact => ({
          ...contact,
          contactId: contact.contactId.toLowerCase()
        }));
      }
      
      return transformedOpportunity;
    } catch (error) {
      throw error;
    }
  },

  // Save opportunity (create or update)
  async saveOpportunity(opportunity, currentUserEmail) {
    try {
      const { apiBaseUrl } = getApiConfig();
      // Determine if this is new based on whether it has a createdByEmail set
      // New opportunities will have empty createdByEmail
      const isNew = !opportunity.createdByEmail || opportunity.createdByEmail === '';
      
      // Set created/modified info
      if (isNew) {
        // Generate a new ID for new opportunities if not already set
        if (!opportunity.opportunityId || opportunity.opportunityId === '') {
          opportunity.opportunityId = uuidv4().toLowerCase();
        }
        opportunity.createdByEmail = currentUserEmail;
        opportunity.createdDate = new Date().toISOString();
      } else {
        opportunity.modifiedByEmail = currentUserEmail;
        opportunity.modifiedDate = new Date().toISOString();
      }

      // Ensure consistent casing for IDs
      if (opportunity.contactIds) {
        opportunity.contactIds = opportunity.contactIds.map(id => id.toLowerCase());
      }
      if (opportunity.contacts) {
        opportunity.contacts = opportunity.contacts.map(contact => ({
          ...contact,
          contactId: contact.contactId.toLowerCase()
        }));
      }

      // Ensure description is included in the payload
      if (opportunity.description === undefined || opportunity.description === null) {
        opportunity.description = '';
      }

      // Handle null or empty company ID
      if (!opportunity.companyId || opportunity.companyId === '') {
        opportunity.companyId = null;
      }

      const method = isNew ? 'POST' : 'PUT';
      const url = isNew 
        ? `${apiBaseUrl}/opportunities`
        : `${apiBaseUrl}/opportunities/${opportunity.opportunityId}`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(opportunity)
      });

      const data = await handleResponse(response);

      // Transform the response data to ensure consistent property names
      const transformedData = transformOpportunity(data);

      return transformedData;
    } catch (error) {
      console.error('Error in saveOpportunity:', error);
      throw error;
    }
  },

  // Get opportunities by stage
  async getOpportunitiesByStage(dealStage) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/opportunities/stage/${dealStage}`);
      return handleResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Get opportunities by company
  async getOpportunitiesByCompany(companyId, pageNumber = 1, pageSize = 5, sortField = 'opportunityName', sortDirection = 'asc') {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(
        `${apiBaseUrl}/opportunities/company/${companyId}?` + 
        `pageNumber=${pageNumber}&pageSize=${pageSize}&` +
        `sortField=${sortField}&sortDirection=${sortDirection}`
      );
      const data = await handleResponse(response);
      return {
        data: Array.isArray(data.data) ? data.data.map(transformOpportunity) : [],
        totalCount: data.totalCount || 0
      };
    } catch (error) {
      throw error;
    }
  },

  // Get opportunities by contact
  async getOpportunitiesByContact(contactId, pageNumber = 1, pageSize = 5, sortField = 'opportunityName', sortDirection = 'asc') {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(
        `${apiBaseUrl}/opportunities/contact/${contactId}?` + 
        `pageNumber=${pageNumber}&pageSize=${pageSize}&` +
        `sortField=${sortField}&sortDirection=${sortDirection}`
      );
      const data = await handleResponse(response);
      return {
        data: Array.isArray(data.data) ? data.data.map(transformOpportunity) : [],
        totalCount: data.totalCount || 0
      };
    } catch (error) {
      throw error;
    }
  },

  // Delete opportunity
  // Check if opportunity can be deleted (has no job history)
  async checkOpportunityDeletion(opportunityId) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/opportunities/${opportunityId}/check-deletion`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to check opportunity deletion status');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error checking opportunity deletion:', error);
      throw error;
    }
  },

  async deleteOpportunity(opportunityId) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/opportunities/${opportunityId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete opportunity');
      }
      
      // For successful deletion (204 No Content), just return true
      if (response.status === 204) {
        return true;
      }

      // For any other success status, try to parse JSON
      return await response.json();
    } catch (error) {
      console.error('Error deleting opportunity:', error);
      throw error;
    }
  },

  // =============================================
  // OPPORTUNITY MOMENTS
  // =============================================

  // Create moment
  async createMoment(opportunityId, message, createdByEmail) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const momentId = uuidv4();
      const createdDate = new Date().toISOString();
      
      const url = `${apiBaseUrl}/opportunities/${opportunityId}/moments`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: momentId,
          message,
          createdByEmail,
          createdDate
        })
      });
      
      const data = await handleResponse(response);

      // Transform the response to match frontend format
      const transformedMoment = {
        id: data.Id || data.id,
        message: data.Message || data.message,
        createdByEmail: data.CreatedByEmail || data.createdByEmail,
        createdDate: data.CreatedDate || data.createdDate,
        createdByName: data.CreatedByName || data.createdByName
      };

      return transformedMoment;
    } catch (error) {
      console.error('createMoment - Error:', error);
      throw error;
    }
  },

  // =============================================
  // OPPORTUNITY COMMENTS
  // =============================================

  // Create comment
  async createComment(opportunityId, commentText, createdByEmail) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const commentId = uuidv4();
      
      const response = await fetch(`${apiBaseUrl}/opportunities/${opportunityId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: commentId,
          commentText,
          createdByEmail
        })
      });
      
      return handleResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Update comment
  async updateComment(opportunityId, commentId, commentText, modifiedByEmail) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/opportunities/${opportunityId}/comments/${commentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          commentText,
          modifiedByEmail,
          modifiedDate: new Date().toISOString()
        })
      });
      
      return handleResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Delete comment
  async deleteComment(opportunityId, commentId) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/opportunities/${opportunityId}/comments/${commentId}`, {
        method: 'DELETE'
      });
      
      return handleResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // =============================================
  // OPPORTUNITY CONTACTS
  // =============================================

  // Add contact to opportunity
  async addContactToOpportunity(opportunityId, contactId, sortOrder = null) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/opportunities/${opportunityId}/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ contactId, sortOrder })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error adding contact to opportunity:', error);
      throw error;
    }
  },

  // Remove contact from opportunity
  async removeContactFromOpportunity(opportunityId, contactId) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/opportunities/${opportunityId}/contacts/${contactId}`, {
        method: 'DELETE'
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error removing contact from opportunity:', error);
      throw error;
    }
  },

  // Update contact order
  async updateContactOrder(opportunityId, contactId, newSortOrder) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/opportunities/${opportunityId}/contacts/${contactId}/order`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ newSortOrder })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error updating contact order:', error);
      throw error;
    }
  },

  // =============================================
  // OPPORTUNITY SALESPERSONS
  // =============================================

  // Add salesperson to opportunity
  async addSalespersonToOpportunity(opportunityId, salespersonEmail, sortOrder = null) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/opportunities/${opportunityId}/salespersons`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ salespersonEmail, sortOrder })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error adding salesperson to opportunity:', error);
      throw error;
    }
  },

  // Remove salesperson from opportunity
  async removeSalespersonFromOpportunity(opportunityId, salespersonEmail) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const encodedEmail = encodeURIComponent(salespersonEmail);
      const response = await fetch(`${apiBaseUrl}/opportunities/${opportunityId}/salespersons/${encodedEmail}`, {
        method: 'DELETE'
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error removing salesperson from opportunity:', error);
      throw error;
    }
  },

  // Update salesperson order
  async updateSalespersonOrder(opportunityId, salespersonEmail, newSortOrder) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const encodedEmail = encodeURIComponent(salespersonEmail);
      const response = await fetch(`${apiBaseUrl}/opportunities/${opportunityId}/salespersons/${encodedEmail}/order`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ newSortOrder })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error updating salesperson order:', error);
      throw error;
    }
  },

  // Add search function for won opportunities
  async searchWonOpportunities(searchTerm) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/opportunities/search/won?term=${encodeURIComponent(searchTerm || '')}`);
      if (!response.ok) {
        throw new Error('Failed to fetch won opportunities');
      }
      return await response.json();
    } catch (error) {
      console.error('Error searching won opportunities:', error);
      throw error;
    }
  }
};